import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import Confetti from "react-confetti";
import { useWindowSize } from "@react-hook/window-size"; // optional
import { MdLocalDining } from "react-icons/md";
import { FaCalendarCheck } from "react-icons/fa";

let hasPlayedConfetti = false; // Prevent repeated bursts

const BookingNotificationModal = ({ isOpen, onClose, orderId, orderInfo }) => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [width, height] = useWindowSize();

  let hasPlayedAudio = false; // Place this **outside** the component

  useEffect(() => {
    if (!isOpen) {
      setShowContent(false);
      return;
    }

    let timeout;

    // Start confetti once per session
    if (!hasPlayedConfetti) {
      setShowConfetti(true);
      hasPlayedConfetti = true;

      // Auto stop confetti after 3 seconds
      setTimeout(() => setShowConfetti(false), 10000);
    }

    const sound = new Audio("/audio/order3.wav");

    const handleInteraction = () => {
      if (!hasPlayedAudio) {
        sound.play().catch(() => {});
        hasPlayedAudio = true;
      }
      window.removeEventListener("click", handleInteraction);
    };

    sound.play().catch(() => {
      window.addEventListener("click", handleInteraction);
    });

    timeout = setTimeout(() => setShowContent(true), 500);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("click", handleInteraction); // Cleanup
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* 🎉 Confetti background */}
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={250}
          recycle={false}
        />
      )}

      <div
        className={`relative w-full max-w-md mx-4 transition-all duration-500 transform border border-redlight rounded-2xl shadow-xl ${
          showContent
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-90 translate-y-4"
        } bg-white/10 backdrop-blur-lg text-white p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow Effect Border Pulse */}
        <div className="absolute inset-0 rounded-2xl border-2 border-yellowlight animate-pulse pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white hover:text-red-500 z-10"
        >
          <IoClose size={24} />
        </button>

        <h2 className="text-2xl font-bold mb-2 flex items-center gap-2 text-redlight">
          <FaCalendarCheck size={28} className="text-green-400" />
          New Booking Received
        </h2>
        <p className="text-yellowlight text-sm mb-4">
          ID:{" "}
          <span className="font-semibold">
            ****{orderId ? orderId.slice(-4) : ""}
          </span>
        </p>

        {showContent ? (
          <>
            <>
              <div className="mb-4 space-y-3">
                {/* Customer */}
                <div>
                  <p className="text-sm font-semibold">Customer:</p>
                  <p className="text-base text-redlight ml-2">
                    {orderInfo?.customer_info?.name || "N/A"}
                  </p>
                </div>

                {/* Services */}
                <div>
                  <p className="text-sm font-semibold">Services:</p>
                  <ul className="list-disc list-inside text-sm mt-1 text-white/90 space-y-1">
                    {orderInfo?.services?.map((srv, idx) => {
                      const price = Number(srv.price) || 0; // ensure it's a number
                      return (
                        <li key={srv._id || idx}>
                          {srv.title} {srv.desc ? `(${srv.desc})` : ""} —{" "}
                          <span className="text-yellow-400 font-medium">
                            ${price.toFixed(2)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Staff */}
                <div>
                  <p className="text-sm font-semibold">Staff:</p>
                  <p className="text-base ml-2">{orderInfo?.staff || "N/A"}</p>
                </div>

                {/* Time */}
                <div>
                  <p className="text-sm font-semibold">Time:</p>
                  <p className="text-base ml-2">{orderInfo?.time || "N/A"}</p>
                </div>

                {/* Payment */}
                <div>
                  <p className="text-sm font-semibold">Payment Method:</p>
                  <p className="text-base capitalize ml-2">
                    {orderInfo?.payments?.paymentMethod || "N/A"}
                  </p>
                </div>

                {/* Total */}
                <div>
                  <p className="text-sm font-semibold">Total:</p>
                  <p className="text-yellow-400 text-base font-bold ml-2">
                    ${orderInfo?.totalPrice?.toFixed(2) || orderInfo?.price}
                  </p>
                </div>
              </div>
            </>

            <button
              onClick={() => {
                onClose();
                // navigate(`/orders/${orderId}`);
                navigate(`/orderscalendar`);
              }}
              className="w-full py-2 mt-4 text-center text-white font-semibold rounded-xl transition hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #f87171, #991b1b)",
              }}
            >
              View Order Details
            </button>
          </>
        ) : (
          <div className="space-y-3 animate-pulse text-sm mt-6">
            <div className="h-4 bg-gray-300/40 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300/40 rounded w-1/2"></div>
            <div className="h-3 bg-gray-300/30 rounded w-full"></div>
            <div className="h-10 bg-gray-400/30 rounded mt-4"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingNotificationModal;
